import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, SwitchCamera } from "lucide-react";

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    osc.start();
    osc.stop(ctx.currentTime + 0.18);
  } catch {}
};

const FACING_KEY = "fas_scanner_facing";
const isBackLabel = (label) => /back|belakang|rear|environment|world/i.test(label || "");

export default function BarcodeScanner({ onResult, onClose }) {
  const [cameras, setCameras] = useState(null); // null = belum terdeteksi
  const [camIdx, setCamIdx] = useState(0);
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const doneRef = useRef(false);

  // deteksi daftar kamera sekali saat modal dibuka
  useEffect(() => {
    let alive = true;
    Html5Qrcode.getCameras()
      .then((cams) => {
        if (!alive) return;
        setCameras(cams || []);
        if (cams && cams.length) {
          let pref = 0;
          try {
            const saved = localStorage.getItem(FACING_KEY);
            if (saved === "user") pref = cams.findIndex((c) => !isBackLabel(c.label));
            if (saved !== "user" || pref < 0) {
              const back = cams.findIndex((c) => isBackLabel(c.label));
              pref = back >= 0 ? back : cams.length - 1;
            }
            if (pref < 0) pref = 0;
          } catch { pref = cams.length - 1; }
          setCamIdx(pref);
        }
      })
      .catch(() => { if (alive) setCameras([]); });
    return () => { alive = false; };
  }, []);

  // mulai scanner setiap kamera terpilih berubah
  useEffect(() => {
    if (cameras === null) return;
    doneRef.current = false;
    setError("");
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      const scanner = new Html5Qrcode("barcode-reader");
      scannerRef.current = scanner;
      const cam = cameras[camIdx];
      const source = cam ? { deviceId: { exact: cam.id } } : { facingMode: "environment" };
      scanner
        .start(source, { fps: 10, qrbox: { width: 260, height: 160 } },
          (text) => {
            if (doneRef.current) return;
            doneRef.current = true;
            beep();
            scanner.stop().then(() => scanner.clear()).catch(() => {});
            onResult(text);
          },
          () => {})
        .catch((e) => {
          if (!cancelled) setError(typeof e === "string" ? e : (e?.message || "Kamera tidak bisa diakses"));
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) { try { if (s.isScanning) s.stop().then(() => s.clear()).catch(() => {}); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras, camIdx]);

  const flip = () => {
    try { const s = scannerRef.current; if (s && s.isScanning) s.stop().then(() => s.clear()).catch(() => {}); } catch {}
    setCamIdx((i) => {
      const next = cameras && cameras.length ? (i + 1) % cameras.length : 0;
      try {
        const cam = cameras[next];
        localStorage.setItem(FACING_KEY, cam && isBackLabel(cam.label) ? "environment" : "user");
      } catch {}
      return next;
    });
  };

  const camLabel = cameras && cameras.length
    ? (isBackLabel(cameras[camIdx]?.label) ? "Belakang" : "Depan")
    : "Belakang";

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="barcode-scanner-modal" className="bg-white rounded-2xl max-w-sm w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="font-display font-black text-stone-900">Scan Barcode Produk</div>
          <div className="flex items-center gap-1.5">
            {cameras && cameras.length > 1 && (
              <button data-testid="barcode-scanner-flip" onClick={flip} title="Ganti kamera depan/belakang" className="h-8 px-3 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold flex items-center gap-1.5 hover:bg-emerald-100 transition">
                <SwitchCamera className="w-4 h-4" /> <span data-testid="barcode-scanner-cam-label">{camLabel}</span>
              </button>
            )}
            <button data-testid="barcode-scanner-close" onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div id="barcode-reader" className="rounded-xl overflow-hidden bg-stone-900 min-h-[220px]" />
        {error && (
          <div data-testid="barcode-scanner-error" className="mt-2 rounded-xl bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-700">
            <b>Kamera gagal dibuka:</b> {error}. Tutup modal lalu coba lagi, dan pastikan akses kamera diizinkan untuk situs ini di pengaturan browser.
          </div>
        )}
        <p className="text-xs text-stone-500 mt-2 text-center">Arahkan kamera ke barcode. {cameras && cameras.length > 1 ? "Tombol Belakang/Depan untuk ganti kamera. " : ""}Izinkan akses kamera jika diminta browser.</p>
      </div>
    </div>
  );
}
